/**
 * The consent decision: cookie persistence plus a tiny observable store so React
 * components and the tracker loaders read from one source of truth.
 */
import { CONSENT_COOKIE, CONSENT_MAX_AGE_DAYS, CONSENT_VERSION, PURPOSES } from './config';

/** Picks the granted flags out of an arbitrary object, one per known integration. */
function grants(source) {
    return Object.fromEntries(PURPOSES.map(purpose => [purpose, source[purpose] === true]));
}

/** Shape of a decision. `decided: false` means the banner still has to be shown. */
const NO_DECISION = {
    decided: false,
    ...grants({}),
    version: CONSENT_VERSION,
    timestamp: null,
};

let state = NO_DECISION;
let hydrated = false;
const listeners = new Set();

export function readCookie(name) {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(
        '(?:^|; )' + name.replace(/([.*+?^$|{}()[\]\\])/g, '\\$1') + '=([^;]*)',
    );
    return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value, maxAgeDays) {
    const attrs = [
        `${name}=${encodeURIComponent(value)}`,
        'path=/',
        `max-age=${Math.round(maxAgeDays * 24 * 60 * 60)}`,
        'samesite=lax',
    ];
    // Secure would make the cookie unwritable on plain-HTTP localhost.
    if (window.location.protocol === 'https:') attrs.push('secure');
    document.cookie = attrs.join('; ');
}

export function deleteCookie(name) {
    // Clear on the exact path we set it on, plus the bare name, and — for the
    // Google cookies — on the registrable domain they may have been scoped to.
    const host = window.location.hostname;
    const domains = [null, host, host.replace(/^www\./, '.')];
    for (const domain of domains) {
        document.cookie = [`${name}=`, 'path=/', 'max-age=0', domain ? `domain=${domain}` : null]
            .filter(Boolean)
            .join('; ');
    }
}

function parse(raw) {
    if (!raw) return NO_DECISION;
    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return NO_DECISION;
    }
    // A decision recorded against an older set of choices has to be re-asked.
    if (!parsed || parsed.version !== CONSENT_VERSION) return NO_DECISION;
    return {
        decided: true,
        ...grants(parsed),
        version: CONSENT_VERSION,
        timestamp: parsed.timestamp || null,
    };
}

/** Reads the cookie once per page load. Safe to call repeatedly. */
export function hydrate() {
    if (hydrated) return state;
    hydrated = true;
    state = parse(readCookie(CONSENT_COOKIE));
    return state;
}

export function getConsent() {
    return hydrated ? state : hydrate();
}

/** Server render has no cookie, so the banner is never part of the static HTML. */
export function getServerConsent() {
    return NO_DECISION;
}

export function subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

function emit(next) {
    state = next;
    hydrated = true;
    for (const listener of listeners) listener();
}

/** Persists a decision and notifies subscribers. */
export function setConsent(selection) {
    const next = {
        decided: true,
        ...grants(selection),
        version: CONSENT_VERSION,
        timestamp: new Date().toISOString(),
    };
    writeCookie(CONSENT_COOKIE, JSON.stringify(next), CONSENT_MAX_AGE_DAYS);
    emit(next);
    return next;
}

/** Forgets the decision so the banner is shown again. */
export function clearConsent() {
    deleteCookie(CONSENT_COOKIE);
    emit(NO_DECISION);
}
